import React, { useEffect, useState } from 'react';
import { accountsReceivableAPI } from '../services/accountsReceivableApi';
import { formatApiError } from '../../../utils/errorMessage';

// Email an invoice to its customer with the PDF attached. When the business
// can take card payments online, the email also has a Pay now button; when it
// cannot, the reason is shown plainly and no payment option is offered.
export default function SendInvoiceModal({ invoice, onClose, onSent }) {
  const [opts, setOpts] = useState(null);
  const [to, setTo] = useState('');
  const [includePay, setIncludePay] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    accountsReceivableAPI.getSendOptions(invoice.id)
      .then((res) => { if (live) { setOpts(res.data); setTo(res.data.to || ''); } })
      .catch((err) => { if (live) setError(formatApiError(err, 'Could not load the invoice.')); });
    return () => { live = false; };
  }, [invoice.id]);

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await accountsReceivableAPI.sendInvoice(invoice.id, { to, include_pay_link: Boolean(opts && opts.pay_link_available && includePay) });
      onSent(res.data);
    } catch (err) {
      setError(formatApiError(err, 'The invoice could not be sent.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Send invoice {invoice.invoice_number}</h3>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3" role="alert">{error}</div>}
        {!opts && !error && <p className="text-sm text-gray-600">Loading...</p>}
        {opts && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="send-invoice-to">Send to</label>
            <input id="send-invoice-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-3" />
            <p className="text-sm text-gray-600 mb-3">The invoice PDF is attached to the email.</p>
            {opts.pay_link_available ? (
              <label className="flex items-center text-sm mb-3">
                <input type="checkbox" className="mr-2" checked={includePay} onChange={(e) => setIncludePay(e.target.checked)} />
                Add a Pay now button (card payment through Stripe)
              </label>
            ) : (
              <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-3">
                This email will not have a Pay now button. {opts.pay_link_note}
              </p>
            )}
            {opts.last_sent_at && (
              <p className="text-xs text-gray-500 mb-3">Last sent {new Date(opts.last_sent_at).toLocaleString()} to {opts.last_sent_to}. Sending again replaces the earlier payment link.</p>
            )}
          </>
        )}
        <div className="flex justify-end space-x-2 mt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded text-gray-700">Cancel</button>
          <button type="button" onClick={send} disabled={!opts || busy || !to}
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50">{busy ? 'Sending...' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}